import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  InputAdornment,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import SearchIcon from "@mui/icons-material/Search";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import Swal from "sweetalert2";
import dayjs from "dayjs";
import api from "../adapters/api";
import { AuthContext } from "../context/AuthContext";
import yearAdapter from "../adapters/yearAdapter";
import { getWsUrl } from "../utils/wsUrl";

const formatBE = (d) => d.format(`DD/MM/${d.year() + 543} HH:mm`);

const CountdownDisplay = React.memo(({ scheduledTime }) => {
  const [text, setText] = useState("");

  useEffect(() => {
    const update = () => {
      const diff = scheduledTime.diff(dayjs(), "second");
      if (diff <= 0) { setText("กำลังนำเข้า..."); return; }
      const h = Math.floor(diff / 3600);
      const m = Math.floor((diff % 3600) / 60);
      const s = diff % 60;
      if (h > 0) setText(`${h} ชม. ${m} น. ${s} วิ.`);
      else if (m > 0) setText(`${m} น. ${s} วิ.`);
      else setText(`${s} วิ.`);
    };
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [scheduledTime]);

  return <span>{text}</span>;
});

const NoRowsOverlay = ({ searchTerm }) => (
  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "text.secondary" }}>
    {searchTerm?.trim() ? `ไม่พบสินค้าที่ตรงกับ "${searchTerm}"` : "ไม่พบข้อมูล"}
  </Box>
);

const PRICE_COLS_DEF = [
  { field: "ic_code", headerName: "รหัสสินค้า", align: "center", headerAlign: "center", width: 120 },
  { field: "ic_name", headerName: "รายการสินค้า", flex: 1, minWidth: 200 },
  { field: "unit_code", headerName: "หน่วย", align: "center", headerAlign: "center", width: 80 },
  {
    field: "ต้นทุน", headerName: "ต้นทุน", align: "center", headerAlign: "center", width: 95,
    renderCell: ({ value }) => <Box sx={{ color: "error.main", fontWeight: "bold" }}>{value}</Box>,
  },
  {
    field: "price_0", headerName: "สด", align: "center", headerAlign: "center", width: 95,
    renderCell: ({ value }) => <Box sx={{ color: "primary.main", fontWeight: "bold" }}>{value}</Box>,
  },
  {
    field: "price_1", headerName: "7%", align: "center", headerAlign: "center", width: 95,
    renderCell: ({ value }) => <Box sx={{ color: "success.main", fontWeight: "bold" }}>{value}</Box>,
  },
  {
    field: "price_2", headerName: "10%", align: "center", headerAlign: "center", width: 95,
    renderCell: ({ value }) => <Box sx={{ color: "warning.main", fontWeight: "bold" }}>{value}</Box>,
  },
  {
    field: "price_3", headerName: "12%", align: "center", headerAlign: "center", width: 95,
    renderCell: ({ value }) => <Box sx={{ color: "text.secondary", fontWeight: "bold" }}>{value}</Box>,
  },
  {
    field: "price_4", headerName: "15%", align: "center", headerAlign: "center", width: 95,
    renderCell: ({ value }) => <Box sx={{ color: "info.main", fontWeight: "bold" }}>{value}</Box>,
  },
  {
    field: "last_edit", headerName: "แก้ไขล่าสุด", align: "center", headerAlign: "center", width: 160,
    sortComparator: (v1, v2) => {
      const parse = (str) => {
        if (!str) return 0;
        const [datePart, timePart = ""] = String(str).split(" ");
        const [d, m, by] = datePart.split("/").map(Number);
        const [h = 0, min = 0, s = 0] = timePart.split(":").map(Number);
        return new Date(by - 543, m - 1, d, h, min, s).getTime();
      };
      return parse(v1) - parse(v2);
    },
    renderCell: ({ row }) => (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", lineHeight: 1.3 }}>
        <Typography variant="caption" display="block">{row.last_edit || "—"}</Typography>
        <Typography variant="caption" color="text.secondary">{row.editor}</Typography>
      </Box>
    ),
  },
];

const GRID_SX = {
  border: "none",
  "& .MuiDataGrid-columnHeader": { bgcolor: "#212121", color: "#fff" },
  "& .MuiDataGrid-columnHeaderTitle": { fontWeight: "bold" },
  "& .MuiDataGrid-sortIcon": { color: "rgba(255,255,255,0.7)" },
  "& .MuiDataGrid-menuIconButton": { color: "rgba(255,255,255,0.7)" },
  "& .MuiDataGrid-columnSeparator": { color: "rgba(255,255,255,0.15)" },
  "& .MuiDataGrid-filler--pinnedLeft, & .MuiDataGrid-filler": { bgcolor: "#212121" },
};

const UpdatePrice = () => {
  const { currentUser } = useContext(AuthContext);
  const [file, setFile] = useState(null);
  const [products, setProducts] = useState([]);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduledTime, setScheduledTime] = useState(null);
  const [isScheduled, setIsScheduled] = useState(false);
  const scheduledJobIdRef = useRef(null);
  const pollIntervalRef   = useRef(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [validationResult, setValidationResult] = useState(null);

  const fetchProducts = async () => {
    setIsLoading(true);
    try {
      const response = await api.post("/api/productsPrice");
      setProducts(response.data);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchLastUpdate = async () => {
    const response = await api.post("/api/lastUpdatePrice");
    setLastUpdate(response.data[0] ?? null);
  };

  useEffect(() => {
    fetchProducts();
    fetchLastUpdate();

    const saved = localStorage.getItem('scheduledJob');
    if (saved) {
      try {
        const { id, scheduled_at, validRowCount, errors } = JSON.parse(saved);
        api.get(`/api/schedule-upload/${id}`).then((res) => {
          const { status } = res.data;
          if (status === 'pending' || status === 'running') {
            scheduledJobIdRef.current = id;
            setScheduledTime(dayjs(scheduled_at));
            setIsScheduled(true);
            if (validRowCount !== undefined) setValidationResult({ validRowCount, errors: errors ?? [] });
            startPolling(id);
          } else {
            localStorage.removeItem('scheduledJob');
          }
        }).catch(() => {
          localStorage.removeItem('scheduledJob');
        });
      } catch (_) {
        localStorage.removeItem('scheduledJob');
      }
    }

    return () => stopPolling();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const wsUrl = getWsUrl();
    const ws = new WebSocket(wsUrl);
    ws.onmessage = async (event) => {
      try {
        const text = event.data instanceof Blob ? await event.data.text() : event.data;
        const msg  = JSON.parse(text);
        if (msg.type === 'PRICE_UPLOAD_DONE' && msg.jobId === scheduledJobIdRef.current) {
          stopPolling();
          handleUploadComplete('done', msg.result_message);
        }
      } catch (_) {}
    };
    return () => ws.close();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredProducts = useMemo(() => {
    const tokens = searchTerm.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (tokens.length === 0) return products;
    return products.filter((p) => {
      const nameText = String(p.ic_name ?? "").toLowerCase();
      const codeText = String(p.ic_code ?? "").toLowerCase();
      const unitText = String(p.unit_code ?? "").toLowerCase();
      return tokens.every((token) => {
        const hasLetter = /\p{L}/u.test(token);
        if (hasLetter) {
          return nameText.includes(token) || codeText.includes(token) || unitText.includes(token);
        }
        return nameText.includes(token);
      });
    });
  }, [products, searchTerm]);

  const stopPolling = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  };

  const handleUploadComplete = async (status, message) => {
    localStorage.removeItem('scheduledJob');
    setIsUploading(false);
    setIsScheduled(false);
    setScheduledTime(null);
    setScheduleEnabled(false);
    setFile(null);
    setFileInputKey((k) => k + 1);
    setValidationResult(null);
    scheduledJobIdRef.current = null;
    if (status === 'cancelled') return;
    if (message === 'success') {
      await Swal.fire({ title: 'นำเข้าข้อมูลสำเร็จ', icon: 'success', timer: 1500, showConfirmButton: false });
    } else if (message?.startsWith('อัพเดทสำเร็จบางส่วน')) {
      await Swal.fire({
        title: 'อัพเดทสำเร็จบางส่วน', icon: 'warning', width: 600,
        html: `<pre style="text-align:left;max-height:300px;overflow:auto;">${message}</pre>`,
        confirmButtonColor: '#1976d2', confirmButtonText: 'ตกลง',
      });
    } else {
      await Swal.fire({
        icon: 'error', title: 'เกิดข้อผิดพลาด', width: 600,
        html: `<pre style="text-align:left;max-height:300px;overflow:auto;">${message}</pre>`,
        confirmButtonColor: '#1976d2', confirmButtonText: 'ตกลง',
      });
    }
    fetchProducts();
    fetchLastUpdate();
  };

  const startPolling = (jobId) => {
    stopPolling();
    pollIntervalRef.current = setInterval(async () => {
      try {
        const res = await api.get(`/api/schedule-upload/${jobId}`);
        const { status, result_message } = res.data;
        if (['done', 'error', 'cancelled'].includes(status)) {
          stopPolling();
          handleUploadComplete(status, result_message);
        }
      } catch (err) {
        console.error('Polling error:', err);
      }
    }, 30_000);
  };

  const scheduleUpload = async () => {
    const formData = new FormData();
    formData.append('file',         file);
    formData.append('username',     currentUser?.user_code ?? '');
    formData.append('scheduled_at', scheduledTime.toISOString());
    try {
      const res = await api.post('/api/schedule-upload', formData, {
        headers:         { 'Content-Type': 'multipart/form-data' },
        withCredentials: true,
      });
      const vResult = { validRowCount: res.data.validRowCount, errors: res.data.errors ?? [] };

      if (vResult.errors.length > 0) {
        const confirm = await Swal.fire({
          icon: 'warning',
          title: `พบข้อมูลที่มีปัญหา ${vResult.errors.length} รายการ`,
          html: `<pre style="text-align:left;max-height:240px;overflow:auto;margin-bottom:12px;">${vResult.errors.join('\n')}</pre>ต้องการกำหนดเวลานำเข้าเฉพาะรายการที่ถูกต้อง <strong>${vResult.validRowCount} รายการ</strong> หรือไม่?`,
          width: 640,
          showCancelButton: true,
          confirmButtonColor: '#d32f2f',
          cancelButtonColor: '#757575',
          confirmButtonText: 'ยืนยันอัพเดท',
          cancelButtonText: 'ยกเลิก',
        });
        if (!confirm.isConfirmed) {
          try { await api.delete(`/api/schedule-upload/${res.data.id}`, { withCredentials: true }); } catch (_) {}
          return false;
        }
      }

      scheduledJobIdRef.current = res.data.id;
      setValidationResult(vResult);
      localStorage.setItem('scheduledJob', JSON.stringify({
        id: res.data.id,
        scheduled_at: res.data.scheduled_at,
        validRowCount: vResult.validRowCount,
        errors: vResult.errors,
      }));
      startPolling(res.data.id);
      return true;
    } catch (err) {
      console.error(err);
      if (err.response?.status === 422) {
        const errors = err.response.data.errors ?? [];
        await Swal.fire({
          icon: 'error',
          title: 'ข้อมูลในไฟล์ไม่ถูกต้อง',
          width: 600,
          html: `<pre style="text-align:left;max-height:300px;overflow:auto;">${errors.join('\n') || 'ไม่มีข้อมูลที่ถูกต้องในไฟล์'}</pre>`,
          confirmButtonColor: '#1976d2',
          confirmButtonText: 'ตกลง',
        });
      } else {
        Swal.fire({
          icon: 'error',
          title: 'ไม่สามารถกำหนดเวลาได้ กรุณาลองใหม่อีกครั้ง',
          confirmButtonColor: '#1976d2',
          confirmButtonText: 'ตกลง',
        });
      }
      return false;
    }
  };

  const handleFileInputChange = (event) => {
    setFile(event.target.files[0] ?? null);
  };

  const performUpload = async () => {
    const formData = new FormData();
    formData.append("username", currentUser?.user_code ?? "");
    formData.append("file", file);

    setIsUploading(true);
    try {
      const response = await api.post("/api/import-csv", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        withCredentials: true,
      });
      await handleUploadComplete('done', response.data);
    } catch (error) {
      console.error(error);
      setIsUploading(false);
      Swal.fire({
        icon: "error",
        title: "เกิดข้อผิดพลาด",
        text: "ไม่สามารถเชื่อมต่อเซิร์ฟเวอร์ได้ กรุณาลองใหม่อีกครั้ง",
        confirmButtonColor: "#1976d2",
        confirmButtonText: "ตกลง",
      });
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!file) {
      Swal.fire({
        icon: "warning",
        title: "กรุณาเลือกไฟล์ CSV",
        confirmButtonColor: "#1976d2",
        confirmButtonText: "ตกลง",
      });
      return;
    }

    if (scheduleEnabled && !scheduledTime) {
      Swal.fire({
        icon: "warning",
        title: "กรุณาระบุวันและเวลาที่ต้องการกำหนด",
        confirmButtonColor: "#1976d2",
        confirmButtonText: "ตกลง",
      });
      return;
    }

    if (scheduleEnabled && scheduledTime) {
      const delay = scheduledTime.valueOf() - Date.now();
      if (delay <= 60_000) {
        Swal.fire({
          icon: "warning",
          title: "กรุณาเลือกเวลาในอนาคตอย่างน้อย 1 นาที",
          confirmButtonColor: "#1976d2",
          confirmButtonText: "ตกลง",
        });
        return;
      }

      const displayTime = formatBE(scheduledTime);
      const result = await Swal.fire({
        title: "ยืนยันการกำหนดเวลานำเข้าข้อมูล?",
        html: `ระบบจะนำเข้าข้อมูลราคาในวันที่<br/><strong>${displayTime} น. (ICT)</strong><br/><br/>ระบบจะนำเข้าข้อมูลอัตโนมัติ ไม่จำเป็นต้องเปิดหน้านี้ค้างไว้`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d32f2f",
        cancelButtonColor: "#757575",
        confirmButtonText: "ยืนยัน",
        cancelButtonText: "ยกเลิก",
      });
      if (!result.isConfirmed) return;

      setIsUploading(true);
      const ok = await scheduleUpload();
      setIsUploading(false);
      if (ok) setIsScheduled(true);
      return;
    }

    const result = await Swal.fire({
      title: "ยืนยันการนำเข้าข้อมูล?",
      text: "ข้อมูลราคาจะถูกอัปเดตและไม่สามารถย้อนกลับได้",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d32f2f",
      cancelButtonColor: "#757575",
      confirmButtonText: "นำเข้าข้อมูล",
      cancelButtonText: "ยกเลิก",
    });
    if (!result.isConfirmed) return;
    performUpload();
  };

  const handleCancelSchedule = async () => {
    const confirm = await Swal.fire({
      icon: 'question',
      title: 'ยกเลิกการนำเข้าข้อมูล?',
      text: 'การนำเข้าข้อมูลจะถูกยกเลิก',
      showCancelButton: true,
      confirmButtonColor: '#d32f2f',
      cancelButtonColor: '#757575',
      confirmButtonText: 'ยกเลิกการนำเข้า',
      cancelButtonText: 'ปิด',
    });
    if (!confirm.isConfirmed) return;

    localStorage.removeItem('scheduledJob');
    stopPolling();
    const jobId = scheduledJobIdRef.current;
    if (jobId) {
      try { await api.delete(`/api/schedule-upload/${jobId}`, { withCredentials: true }); } catch (_) {}
      scheduledJobIdRef.current = null;
    }
    setIsScheduled(false);
    setValidationResult(null);
  };

  return (
    <LocalizationProvider dateAdapter={yearAdapter} adapterLocale="th">
      <Box sx={{ px: 2, display: "flex", flexDirection: "column", height: "calc(100vh - var(--nav-h, 64px))" }}>
        {/* Sticky toolbar */}
        <Box
          sx={{
            position: "sticky",
            top: "var(--nav-h)",
            zIndex: 10,
            bgcolor: "background.paper",
            borderBottom: 1,
            borderColor: "divider",
            py: 1,
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1.5,
            flexShrink: 0,
          }}
        >
          {/* Left: last-update text + search + row count */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
            {lastUpdate ? (
              <Typography variant="body2" color="text.secondary">
                แก้ไขล่าสุดโดย: <strong>{lastUpdate.editor}</strong>
                {" — "}
                {lastUpdate.last_update}
              </Typography>
            ) : (
              <Typography variant="body2" color="text.disabled">
                ยังไม่มีข้อมูลการแก้ไข
              </Typography>
            )}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TextField
                size="small"
                placeholder="ค้นหารหัส / รายการสินค้า / หน่วย..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ width: 300 }}
              />
              {!isLoading && (
                <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
                  {filteredProducts.length.toLocaleString()} รายการ
                </Typography>
              )}
            </Box>
          </Box>

          {/* Right: scheduled indicator OR upload form */}
          {isScheduled ? (
            <Box
              key="scheduled-bar"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                bgcolor: "warning.light",
                color: "warning.contrastText",
                px: 2,
                py: 1,
                borderRadius: 1,
              }}
            >
              <AccessTimeIcon />
              <Box>
                <Typography variant="body2" fontWeight="bold">
                  กำหนดนำเข้า: {formatBE(scheduledTime)} น. (ICT)
                </Typography>
                <Typography variant="caption">
                  เหลืออีก: <CountdownDisplay scheduledTime={scheduledTime} />
                </Typography>
                {validationResult && (
                  <Typography variant="caption" sx={{ display: "block", mt: 0.25 }}>
                    ตรวจสอบแล้ว: {validationResult.validRowCount} รายการ
                    {validationResult.errors.length > 0 && (
                      <>
                        {" | มีปัญหา: "}
                        <Box
                          component="span"
                          sx={{ cursor: "pointer", textDecoration: "underline" }}
                          onClick={() =>
                            Swal.fire({
                              icon: "warning",
                              title: `มีปัญหา ${validationResult.errors.length} รายการ (จะถูกข้ามเมื่อนำเข้า)`,
                              html: `<pre style="text-align:left;max-height:300px;overflow:auto;">${validationResult.errors.join("\n")}</pre>`,
                              width: 600,
                              confirmButtonColor: "#1976d2",
                              confirmButtonText: "ตกลง",
                            })
                          }
                        >
                          {validationResult.errors.length} รายการ (ดูรายละเอียด)
                        </Box>
                      </>
                    )}
                  </Typography>
                )}
              </Box>
              <Button
                size="small"
                color="error"
                variant="outlined"
                onClick={handleCancelSchedule}
                sx={{ ml: 0.5, whiteSpace: "nowrap" }}
              >
                ยกเลิกการนำเข้า
              </Button>
            </Box>
          ) : (
            <Box
              key="upload-form"
              component="form"
              onSubmit={handleSubmit}
              sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}
            >
              <Button
                key={fileInputKey}
                variant="outlined"
                component="label"
                size="small"
                startIcon={<UploadFileIcon />}
              >
                เลือกไฟล์ CSV
                <input
                  type="file"
                  hidden
                  accept=".csv"
                  onChange={handleFileInputChange}
                />
              </Button>
              {file && (
                <Typography variant="body2" color="text.secondary">
                  {file.name}
                </Typography>
              )}

              <FormControlLabel
                control={
                  <Switch
                    checked={scheduleEnabled}
                    onChange={(e) => {
                      setScheduleEnabled(e.target.checked);
                      if (!e.target.checked) setScheduledTime(null);
                    }}
                    size="small"
                  />
                }
                label={<Typography variant="body2">กำหนดเวลา</Typography>}
                sx={{ ml: 0 }}
              />

              {scheduleEnabled && (
                <DateTimePicker
                  value={scheduledTime}
                  onChange={setScheduledTime}
                  format="DD/MM/YYYY HH:mm"
                  ampm={false}
                  disablePast
                  slotProps={{ textField: { size: "small", sx: { width: 220 } } }}
                />
              )}

              <Button
                type="submit"
                variant="contained"
                size="small"
                disabled={isUploading}
                startIcon={
                  isUploading ? <CircularProgress size={16} color="inherit" /> : null
                }
              >
                {isUploading ? "กำลังนำเข้า..." : "นำเข้าข้อมูล"}
              </Button>
            </Box>
          )}
        </Box>

        {/* Virtualized price grid */}
        <Box
          sx={{
            flex: 1,
            mt: 1,
            mb: 1,
            minHeight: 0,
            border: 1,
            borderColor: "divider",
            borderRadius: 1,
            overflow: "hidden",
          }}
        >
          <DataGrid
            rows={filteredProducts}
            columns={PRICE_COLS_DEF}
            getRowId={(row) => `${row.ic_code}_${row.unit_code}`}
            loading={isLoading}
            rowHeight={48}
            disableRowSelectionOnClick
            disableColumnMenu
            hideFooter
            initialState={{
              sorting: { sortModel: [{ field: "last_edit", sort: "desc" }] },
            }}
            sx={GRID_SX}
            slots={{ noRowsOverlay: NoRowsOverlay }}
            slotProps={{ noRowsOverlay: { searchTerm } }}
          />
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default UpdatePrice;
